import {
  uniqueStandardRefsForSetFromHits,
} from '@/api/search'
import {
  multiSearchAlgolia,
  facetValuesWithResultsFromAlgoliaFacetResponse
} from '@/api/algolia'
import { getStudents } from '@/api/library'
import { getStandardsQuery, getGoalCharacteristics } from '@/api/pathways'
import {
  RestrictionTypePremium,
} from '@/components/search/browse_goal_card_restrictions'
import { isGoalPremium } from '@/util/goal_scores'
import { ALGOLIA_BYTE_LIMIT, isQueryTooLong, TRUNCATED_LIMIT } from '@/util/browse'

export default {
  namespaced: true,
  state: {
    filters: {
      grades: [],
      subject: '',
      subjectDomain: '',
      standard: '',
      keyword: '',
      levelOfSupport: 0,
      characteristics: []
    },
    filterOptions: {
      grades: [
        'Birth-3',
        'Pre-K',
        'K',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        'Adult-Transition'
      ],
      enabledGrades: [],
      subjects: [],
      enabledDomains: [],
      standards: [],
      characteristics: [],
      enabledSubjectsForCharacteristic: [],
      enabledCharacteristicsForSubject: [],

      // tracked to make use of algolia analytics
      algoliaQueryID: ''
    },
    wizardInfo: {
      allStudents: []
    },
    wizardFocus: false,
    goals: [],
    totalResults: 0,
    loadingResults: false,
    loadingStandards: false,
    rawSubjectData: [],
    contentProfileSubject: {
      categories: [],
      grades: []
    }
  },
  mutations: {
    setFilters(state, filters) {
      state.filters = Object.assign(state.filters, filters)
      if (filters.grades && filters.grades.length > 0) {
        state.filters.grades = ensureArray(filters.grades)
      }

      if (!isNaN(parseInt(filters.levelOfSupport))) {
        state.filters.levelOfSupport = parseInt(filters.levelOfSupport)
      } else {
        state.filters.levelOfSupport = 0
      }

      if (filters.characteristics && filters.characteristics.length > 0) {
        state.filters.characteristics = normalizeCharacteristics(state, filters.characteristics)
      } else {
        state.filters.characteristics = []
      }
    },
    setWizardFocus(state, isFocused) {
      state.wizardFocus = isFocused
    },
    setLoadingResults(state, bool) {
      state.loadingResults = bool
    },
    setLoadingStandards(state, bool) {
      state.loadingStandards = bool
    },
    setGoals(state, goals) {
      state.goals = goals
    },
    setTotalResults(state, total) {
      state.totalResults = total
    },
    setRawSubjectData(state, subjects) {
      state.rawSubjectData = subjects
    },
    setInitialFilters(state, filters) {
      if (filters.subject) {
        state.filters.subject = filters.subject
      }
      if (filters.grades && filters.grades.length > 0) {
        state.filters.grades = ensureArray(filters.grades)
      } else {
        state.filters.grades = []
      }
      if (filters.subjectDomain) {
        state.filters.subjectDomain = filters.subjectDomain
      } else {
        state.filters.subjectDomain = ''
      }
      if (filters.standard) {
        state.filters.standard = filters.standard
      } else {
        state.filters.standard = ''
      }
      if (filters.keyword) {
        state.filters.keyword = filters.keyword
      } else {
        state.filters.keyword = ''
      }
      if (!isNaN(parseInt(filters.levelOfSupport))) {
        state.filters.levelOfSupport = parseInt(filters.levelOfSupport)
      } else {
        state.filters.levelOfSupport = 0
      }
      if (filters.characteristics && filters.characteristics.length > 0) {
        state.filters.characteristics = normalizeCharacteristics(state, filters.characteristics)
      } else {
        state.filters.characteristics = []
      }
    },
    setStandardOptions(state, standardOptions) {
      state.filterOptions.standards = standardOptions || []
    },
    setEnabledGrades(state, enabledGrades) {
      state.filterOptions.enabledGrades = enabledGrades || []
    },
    setEnabledSubjectDomains(state, enabledDomains) {
      state.filterOptions.enabledDomains = enabledDomains || []
    },
    setEnabledSubjectsForCharacteristic(state, subjects) {
      state.filterOptions.enabledSubjectsForCharacteristic = subjects || []
    },
    setEnabledCharacteristicsForSubject(state, characteristics) {
      state.filterOptions.enabledCharacteristicsForSubject = characteristics || []
    },
    clearNonSubjectFilters(state) {
      state.filters.subjectDomain = ''
      state.filters.standard = ''
    },
    setWizardStudents(state, students) {
      state.wizardInfo.allStudents = students
    },
    setCPSubject(state, subject) {
      state.contentProfileSubject = subject
    },
    setAlgoliaQueryID(state, id) {
      state.algoliaQueryID = id
    },
    setCharacteristicsOptionsFromAPI(state, characteristics) {
      state.filterOptions.characteristics = characteristics || [];
    }
  },
  getters: {
    filters(state) {
      return state.filters
    },
    filterOptions(state) {
      return state.filterOptions
    },
    enabledGrades(state) {
      return state.filterOptions.enabledGrades
    },
    enabledDomains(state) {
      return state.filterOptions.enabledDomains
    },
    loadingResults(state) {
      return state.loadingResults
    },
    loadingStandards(state) {
      return state.loadingStandards
    },
    longRefToStandardMappings(state) {
      var mapping = {}
      state.filterOptions.standards.forEach(standard => {
        if (standard.standard_long_ref && standard.standard_short_ref) {
          mapping[standard.standard_long_ref] = standard
        }
      })
      return mapping
    },
    characteristics(state) {
      return state.filterOptions.characteristics
    },
    selectedCharacteristicLabel(state) {
      if (!state.filters.characteristics || !state.filters.characteristics.length) return ''

      const allCharacteristics = state.filterOptions.characteristics || []
      const selectedChar = allCharacteristics.find(c => c.Uuid === state.filters.characteristics[0])

      return selectedChar ? selectedChar.Label : ''
    },
    enabledSubjectsSet(state) {
      const subjects = state.filterOptions.enabledSubjectsForCharacteristic || []
      return new Set(subjects.map(s => (s || '').toString().trim().toLowerCase()))
    },
    enabledCharacteristicsSet(state) {
      return new Set(state.filterOptions.enabledCharacteristicsForSubject)
    },
  },
  actions: {
    fetchGoals(context, { standardSets, doNotRetrieveStandards }) {
      let filters = context.state.filters
      if (isQueryTooLong(filters.keyword, ALGOLIA_BYTE_LIMIT)) {
        filters = {...filters, keyword: filters.keyword.substring(0, TRUNCATED_LIMIT) }
      }
      context.commit('setLoadingResults', true)
      if (!doNotRetrieveStandards) {
        context.commit('setLoadingStandards', true)
      }

      // we always want to fetch goals that are "universal"
      if (standardSets.indexOf('universal') < 0) {
        standardSets = standardSets.concat(['universal'])
      }

      multiSearchAlgolia(filters, standardSets, 300).then(data => {
        context.commit('setLoadingResults', false)

        if (data.queryID) {
          context.commit('setAlgoliaQueryID', data.queryID)
        } else {
          context.commit('setAlgoliaQueryID', '')
        }

        let { grades, domains } = facetValuesWithResultsFromAlgoliaFacetResponse(data.facets)

        // subjects available for currently selected characteristic
        const subjectsWithResults = Object.keys((data.facets && data.facets.subject) || {})
        context.commit('setEnabledSubjectsForCharacteristic', subjectsWithResults)

        // characteristics available for currently selected subject
        const characteristicsWithResults = Object.keys((data.facets && data.facets.characteristics) || {})
        context.commit('setEnabledCharacteristicsForSubject', characteristicsWithResults)

        if (grades) {
          context.commit('setEnabledGrades', grades)
        } else if (filters.subject) {
          // if no grade options are returned then all grades are acceptable
          context.commit('setEnabledGrades', context.state.contentProfileSubject.grades)
        } else {
          // if subject is not selected we use default grades list
          context.commit('setEnabledGrades', context.state.filterOptions.grades)
        }

        if (domains) {
          context.commit('setEnabledSubjectDomains', domains)
        } else {
          // all domains can be selected
          context.commit('setEnabledSubjectDomains', context.state.contentProfileSubject.categories)
        }

        context.commit(
          'setGoals',
          processGoals(data.hits, {
            userIsAuthenticated: context.rootGetters['user/isAuthenticated'],
            userIsTrial: context.rootGetters['user/isTrial'],
          })
        )
        context.commit('setTotalResults', data.nbHits)

        const dropdownStandardSet = context.rootGetters['contentProfile/dropdownStandardSetForSubject'](filters.subject)
        let standards = uniqueStandardRefsForSetFromHits(data.hits, dropdownStandardSet)
        if (!standards || standards.length === 0) {
          context.commit('setStandardOptions', [])
          context.commit('setLoadingStandards', false)
        } else if (standards && filters.subject && !doNotRetrieveStandards) {
          console.log('Fetching ' + standards.length + ' standards')
          getStandardsQuery(
            dropdownStandardSet,
            {
              select_fields: ['standard_text', 'standard_short_ref'],
              standards: standards
            }
          ).then(standards => {
            context.commit('setStandardOptions', standards)
            context.commit('setLoadingStandards', false)
          })
        }

        return data
      })
    },
    getWizardStudents(context, userUuid) {
      return getStudents(userUuid).then(data => {
        console.log(data)
        context.commit('setWizardStudents', data.collections)
      })
    },
    fetchGoalCharacteristics(context) {
      if (context.state.filterOptions.characteristics && context.state.filterOptions.characteristics.length) {
        return Promise.resolve()
      }
      return getGoalCharacteristics().then(data => {
        context.commit('setCharacteristicsOptionsFromAPI', data)
        // Normalize any pre-existing deeplinked characteristics (labels -> UUIDs)
        context.commit('setFilters', context.state.filters)
      }).catch(err => {
        console.error(err)
      })
    }
  }
}

function processGoals(goals, { userIsAuthenticated, userIsTrial }) {
  if (userIsTrial || !userIsAuthenticated) {
    return goals.map(goal => {
      if (isGoalPremium(goal)) {
        goal.restrictionType = RestrictionTypePremium
      }
      return goal
    })
  }
  return goals
}

// Caller is responsible to check if val is empty array/null/undefined
function ensureArray(val) {
  if (Array.isArray(val)) {
    return val
  }
  return [val]
}

// Convert provided characteristic identifiers (label or Uuid) to an array of Uuids
function normalizeCharacteristics(state, values) {
  const arr = ensureArray(values)
  const options = state.filterOptions.characteristics || []
  return arr
    .map(v => {
      if (!v) return null
      // Already a UUID?
      const byUuid = options.find(o => o.Uuid === v)
      if (byUuid) return byUuid.Uuid
      // Try match by label (exact match)
      const byLabel = options.find(o => o.Label === v)
      return byLabel ? byLabel.Uuid : null
    })
    .filter(Boolean)
}

